import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  formatDate,
  formatDateTime,
  formatUsd,
  type AdminUser,
  type AdminUserSort,
  type UserKind,
} from '@ytclip/shared'
import { Ban, Monitor, Search, ShieldCheck, Undo2, Users, Wallet } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { SelectField, TextField } from '@/components/ui/field.tsx'
import { Badge, EmptyState, ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { PageHeader, Pagination, SortableTh, TableScroller, Td, Th } from '@/components/ui/misc.tsx'
import { unblockUser } from '@/features/admin/api.ts'
import { useAdminUsers } from '@/features/shared-hooks.ts'
import { ApiRequestError } from '@/lib/api-client.ts'
import { useDebounced } from '@/lib/use-debounced.ts'
import { AdjustDialog, BlockDialog } from './user-dialogs.tsx'

function describe(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback
}

export function AdminUsersPage() {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<AdminUserSort>('joinedDesc')
  const [kind, setKind] = useState<UserKind | 'all'>('all')
  const [page, setPage] = useState(1)
  const [blocking, setBlocking] = useState<AdminUser | null>(null)
  const [adjusting, setAdjusting] = useState<AdminUser | null>(null)

  const queryClient = useQueryClient()
  // Typing filters on the server, so it waits for the typing to stop.
  const debouncedSearch = useDebounced(search, 300)
  const query = {
    page,
    pageSize: 20,
    sort,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(kind === 'all' ? {} : { kind }),
  }
  const { data, isPending, isError } = useAdminUsers(query)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })

  const unblock = useMutation({
    mutationFn: (userId: string) => unblockUser(userId),
    onSuccess: () => {
      toast.success('Akun diaktifkan kembali')
      void invalidate()
    },
    onError: (error) => toast.error(describe(error, 'Gagal mengaktifkan akun')),
  })

  return (
    <>
      <PageHeader title="Pengguna" description="Cari, blokir, dan koreksi saldo pengguna" />

      <Card>
        <CardBody className="space-y-4">
          <div className="grid gap-3 md:grid-cols-[1fr_auto]">
            <TextField
              label="Cari"
              placeholder="Nama, email, atau ID akun"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              prefix="⌕"
            />
            <SelectField
              label="Jenis akun"
              containerClassName="md:w-52"
              value={kind}
              onChange={(event) => {
                setKind(event.target.value as UserKind | 'all')
                setPage(1)
              }}
            >
              <option value="all">Semua</option>
              <option value="web">Web</option>
              <option value="device">Perangkat</option>
            </SelectField>
          </div>

          {isPending ? (
            <LoadingBlock />
          ) : isError ? (
            <ErrorState message="Daftar pengguna tidak bisa dimuat." />
          ) : data.data.length === 0 ? (
            <EmptyState
              icon={debouncedSearch ? <Search /> : <Users />}
              title={debouncedSearch ? 'Tidak ada yang cocok' : 'Belum ada pengguna'}
            />
          ) : (
            <>
              <TableScroller>
                <table className="w-full min-w-3xl border-collapse">
                  <thead>
                    <tr className="border-b border-outline-variant">
                      <Th>Pengguna</Th>
                      <Th className="text-right">Total top-up</Th>
                      <Th>Status</Th>
                      <SortableTh
                        label="Bergabung"
                        direction={sort === 'joinedAsc' ? 'asc' : 'desc'}
                        onToggle={() => {
                          setSort(sort === 'joinedDesc' ? 'joinedAsc' : 'joinedDesc')
                          setPage(1)
                        }}
                      />
                      <Th><span className="sr-only">Aksi</span></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-outline-variant/50 transition-colors hover:bg-surface-container-low"
                      >
                        <Td>
                          <UserIdentity user={user} />
                        </Td>
                        <Td className="text-right tabular-nums">
                          {formatUsd(user.lifetimeTopupUsd)}
                        </Td>
                        <Td>
                          <UserStatus user={user} />
                        </Td>
                        <Td className="whitespace-nowrap text-label-md">
                          {formatDate(user.createdAt)}
                        </Td>
                        <Td className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="text"
                              size="sm"
                              onClick={() => setAdjusting(user)}
                              icon={<Wallet className="size-4" />}
                            >
                              Saldo
                            </Button>
                            {user.isSuperAdmin ? null : user.bannedAt ? (
                              <Button
                                variant="text"
                                size="sm"
                                loading={unblock.isPending}
                                onClick={() => unblock.mutate(user.id)}
                                icon={<Undo2 className="size-4" />}
                              >
                                Aktifkan
                              </Button>
                            ) : (
                              <Button
                                variant="text"
                                size="sm"
                                onClick={() => setBlocking(user)}
                                icon={<Ban className="size-4" />}
                                className="text-error hover:bg-error/10"
                              >
                                Blokir
                              </Button>
                            )}
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>

              <Pagination
                page={data.pagination.page}
                totalPages={data.pagination.totalPages}
                total={data.pagination.total}
                onPageChange={setPage}
              />
            </>
          )}
        </CardBody>
      </Card>

      <BlockDialog user={blocking} onClose={() => setBlocking(null)} onDone={invalidate} />
      <AdjustDialog user={adjusting} onClose={() => setAdjusting(null)} onDone={invalidate} />
    </>
  )
}

function UserStatus({ user }: { user: AdminUser }) {
  if (user.isSuperAdmin) {
    return (
      <Badge tone="primary">
        <ShieldCheck className="size-3" aria-hidden="true" />
        Super admin
      </Badge>
    )
  }

  if (user.bannedAt) {
    return <Badge tone="error">Diblokir</Badge>
  }

  return <Badge tone={user.isProvisioned ? 'success' : 'neutral'}>
    {user.isProvisioned ? 'Aktif' : 'Belum disiapkan'}
  </Badge>
}

/**
 * A device account has no name worth showing and a synthetic email that would
 * only be noise, so it is identified by the id support asks for, with the
 * little that is actually known about the installation underneath.
 */
function UserIdentity({ user }: { user: AdminUser }) {
  if (user.kind === 'device') {
    const seen = user.device?.lastSeenAt

    return (
      <div>
        <span className="flex items-center gap-1.5 font-mono text-label-md text-on-surface">
          <Monitor className="size-3.5 shrink-0 text-on-surface-variant" aria-hidden="true" />
          {user.id}
        </span>
        <span className="block text-label-sm text-on-surface-variant">
          {[user.device?.platform, user.device?.appVersion].filter(Boolean).join(' · ') ||
            'Perangkat'}
          {seen ? ` · terakhir ${formatDateTime(seen)}` : null}
        </span>
      </div>
    )
  }

  return (
    <div>
      <span className="block font-medium text-on-surface">{user.name}</span>
      <span className="block text-label-sm text-on-surface-variant">{user.email}</span>
    </div>
  )
}
